import {
  Box,
  BoxProps,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
  colors,
  lighten,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useAppStore } from "@/global/store";
import { findEventByEventCode } from "../utils.cart";
import { selectEventsSelected } from "@/global/store/data/purchaseSeats/events";
import {
  selectCartEvents,
  selectPerkItemSelected,
} from "@/global/store/data/purchaseSeats/cartManager";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import { PerkItemType, PerkParentType } from "./perks.types";
import { convertDate, convertTime } from "@/global/utils/dateTimeConvert";
import { TbShirtSport } from "react-icons/tb";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import FastfoodOutlinedIcon from "@mui/icons-material/FastfoodOutlined";
import { MdOutlineEventSeat } from "react-icons/md";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { calcPerkItemPrice, findPerkItemOverride } from "../utils.perks";
import { EventLogo } from "@/shared/common/EventCard/event_card_helpers";
import { blurDataURL } from "@/global/common/blur-data-url";
import NextImage from "next/image";

export const PerkParentItemIcon = (props: {
  perkParent: PerkParentType;
  style?: React.CSSProperties;
}) => {
  if (props.perkParent.perkCode.includes("parking"))
    return <DirectionsCarOutlinedIcon sx={props?.style} />;

  switch (props.perkParent.perkCode) {
    case "merchandise":
      return <TbShirtSport style={props?.style} />;
    case "overflow":
      return <MdOutlineEventSeat style={props?.style} />;
    // TODO : need to change access and default
    case "access":
      return <FastfoodOutlinedIcon sx={props?.style} />;
  }
};

export const PerkItemCardWrapper = (props: {
  isSelected?: boolean;
  isDisabled?: boolean;
  children?: React.ReactNode;
  sx?: BoxProps["sx"];
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        pointerEvents: props?.isDisabled ? "none" : "auto",
        opacity: props?.isDisabled ? 0.5 : 1,
        cursor: "pointer",
        width: "100%",
        backgroundColor: props?.isSelected
          ? lighten(theme.palette.primary.main, 0.97)
          : colors.grey[50],
        border: `1px solid ${
          props?.isSelected
            ? lighten(theme.palette.primary.main, 0.3)
            : theme.palette.divider
        }`,
        borderRadius: 1,
        transition: "all 0.2s ease",
        ...props?.sx,
      }}
    >
      {props?.children}
    </Box>
  );
};

export const PerkItemEventDetail = (props: { perkItem: PerkItemType }) => {
  const eventsSelected = useAppStore(selectEventsSelected);
  const perkItemEvent = findEventByEventCode(eventsSelected, props.perkItem.gameEventCode); // prettier-ignore
  const { uiConfigs, purchaseSeatConfigs } = useTeamConfig();
  const shouldDisplayServiceFee = uiConfigs?.shouldDisplayServiceFee ?? true;

  if (!perkItemEvent) return null;

  return (
    <Stack
      direction={"row"}
      spacing={1}
      alignItems={"center"}
      justifyContent={"center"}
    >
      {perkItemEvent.backgroundUrl || perkItemEvent.logoUrl ? (
        <EventLogo
          src={
            (purchaseSeatConfigs?.useBackgroundUrlForEventLogo
              ? perkItemEvent.backgroundUrl
              : perkItemEvent?.logoUrl) as string
          }
          width={36}
          height={36}
        />
      ) : null}
      <Stack>
        <Typography fontWeight={"600"} variant="body2" lineHeight={1.1}>
          {perkItemEvent?.title}
        </Typography>
        <Typography
          variant="body2"
          lineHeight={1.1}
          mt={0.5}
          color={"text.secondary"}
        >
          {convertDate(perkItemEvent.date)} {convertTime(perkItemEvent?.time)}
        </Typography>

        {/** tdc service fee */}
        {props?.perkItem?.serviceFee &&
        props?.perkItem?.serviceFee > 0 &&
        shouldDisplayServiceFee ? (
          <Typography variant="body2" color={"text.secondary"}>
            Service Fee
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
};

export const PerkItemTitleDesc = (props: {
  perkItem: PerkItemType;
  perkParent?: any;
}) => {
  const teamConfig = useTeamConfig();
  const { uiConfigs } = teamConfig;
  const shouldDisplayServiceFee = uiConfigs?.shouldDisplayServiceFee ?? true;
  const overrideTitle = findPerkItemOverride(props?.perkItem, teamConfig)?.title; // prettier-ignore
  const customOverFlowTitle =
    props?.perkItem?.title === "Overflow" &&
    uiConfigs?.inventoryCardSettings?.customOverflowLabel
      ? uiConfigs?.inventoryCardSettings?.customOverflowLabel
      : overrideTitle || props?.perkItem?.title;

  return (
    <Stack>
      {props?.perkItem?.title ? (
        <Typography fontWeight={"600"} variant="body2" lineHeight={1.1}>
          {customOverFlowTitle}
        </Typography>
      ) : null}

      {props?.perkItem?.description &&
      props?.perkParent?.groupingType != "single-select" ? (
        <Typography
          variant="body2"
          lineHeight={1.1}
          mt={0.5}
          color={"text.secondary"}
          dangerouslySetInnerHTML={{
            __html: props?.perkItem?.description,
          }}
        />
      ) : null}

      {/** tdc service fee */}
      {props?.perkItem?.serviceFee &&
      props?.perkItem?.serviceFee > 0 &&
      shouldDisplayServiceFee ? (
        <Typography variant="body2" color={"text.secondary"}>
          Service Fee
        </Typography>
      ) : null}
    </Stack>
  );
};

export const PerkItemCounter = (props: {
  initialValue: number;
  increment: number;
  onChange?: (value: number) => void;
  maxCount: number;
  minCount: number;
  isDisabled?: boolean;
}) => {
  const theme = useTheme();
  const [value, setValue] = useState(props.initialValue);

  const ActionButton = (actionProps: {
    onClick: () => void;
    children: React.ReactNode;
    disabled?: boolean;
  }) => {
    return (
      <IconButton
        disabled={actionProps.disabled || props.isDisabled}
        size="small"
        onClick={(ev) => {
          ev.stopPropagation();
          actionProps.onClick();
        }}
        sx={{
          color: colors.grey[800],
          backgroundColor: `${theme.palette.common.white} !important`,
          border: `1px solid ${theme.palette.divider}`,
          "&:hover": { backgroundColor: `${colors.grey[100]} !important` },
        }}
      >
        {actionProps.children}
      </IconButton>
    );
  };

  const handleIncrement = () => {
    const newValue = value + props.increment;
    if (newValue <= props.maxCount) {
      setValue(newValue);
      props?.onChange?.(newValue);
    }
  };

  const handleDecrement = () => {
    const newValue = value - props.increment;
    if (newValue >= props.minCount) {
      setValue(newValue);
      props?.onChange?.(newValue);
    }
  };

  return (
    <Stack direction={"row"} spacing={0.5} sx={{ userSelect: "none" }}>
      <ActionButton
        onClick={handleDecrement}
        disabled={value <= props.minCount}
      >
        <RemoveRoundedIcon />
      </ActionButton>

      <Stack
        justifyContent={"center"}
        alignItems={"center"}
        border={`1px solid ${theme.palette.divider}`}
        borderRadius={0.7}
        width={"36px"}
        fontSize={"1rem"}
        fontWeight={"500"}
        sx={{
          backgroundColor: theme.palette.common.white,
          opacity: props.isDisabled ? 0.5 : 1,
        }}
      >
        {value}
      </Stack>

      <ActionButton
        onClick={handleIncrement}
        disabled={value >= props.maxCount}
      >
        <AddRoundedIcon />
      </ActionButton>
    </Stack>
  );
};

export const PerkItemPrice = (props: { perkItem: PerkItemType }) => {
  const teamConfig = useTeamConfig();
  const shouldDisplayServiceFee = teamConfig?.uiConfigs?.shouldDisplayServiceFee ?? true; // prettier-ignore
  const perkItemInCart = useAppStore(selectPerkItemSelected(props.perkItem));

  const perkPrice = calcPerkItemPrice(props?.perkItem, teamConfig);
  const value = perkItemInCart?.perkCode === "foodUpgrade" ? teamConfig?.uiConfigs?.inventoryCardSettings?.FBValue : perkPrice; // prettier-ignore

  return (
    <Stack>
      <Typography
        variant="body2"
        color={"text.secondary"}
        sx={{ fontWeight: 500 }}
      >
        {value}
      </Typography>

      {/** tdc service fee */}
      {!!props?.perkItem?.serviceFee &&
      props?.perkItem?.serviceFee > 0 &&
      shouldDisplayServiceFee ? (
        <Stack>
          <Typography variant="body2" color={"text.secondary"}>
            $
            {Number(
              props?.perkItem?.serviceFee * props?.perkItem?.seatCount
            ).toFixed(2)}
          </Typography>
        </Stack>
      ) : null}
    </Stack>
  );
};

export const PerkItemActionElements = (props: {
  perkItem: PerkItemType;
  manualSeatCount: number;
  onSelectPerk?: (params: {
    perkItem: PerkItemType;
    manualSeatCount?: number | null;
  }) => void;
  onChangeManualSeatCount?: (value: number) => void;
}) => {
  const { sm: smDown, lg: lgDown } = useCssMediaQuery("down");
  const cartEvents = useAppStore(selectCartEvents);
  const cartEvent = findEventByEventCode(cartEvents, props?.perkItem?.gameEventCode); // prettier-ignore
  const perkItemSelected = useAppStore(selectPerkItemSelected(props.perkItem)); // prettier-ignore
  const isPerkItemSelected = perkItemSelected !== null;
  const shouldDisplayCounter = [
    "manual",
    "event-manual",
    "suite-additional-manual",
  ].includes(props?.perkItem?.seatCountVariation);
  const shouldDisplayAddRemove = !props?.perkItem?.isMandatory;

  if (!shouldDisplayCounter && !shouldDisplayAddRemove) return null;

  return (
    <>
      {lgDown ? (
        <Divider
          orientation="horizontal"
          sx={{
            width: "100%",
            marginTop: `0px !important`,
            marginBottom: `0px !important`,
          }}
        />
      ) : null}
      <Box p={2} width={lgDown ? "100%" : "initial"}>
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          {shouldDisplayCounter && props?.perkItem?.seatCount ? (
            <Box>
              <PerkItemCounter
                initialValue={props?.perkItem?.seatCount } // prettier-ignore
                increment={props?.perkItem?.increment || 1}
                minCount={props?.perkItem?.minCount}
                maxCount={props?.perkItem?.maxCount}
                onChange={props?.onChangeManualSeatCount}
              />
            </Box>
          ) : null}

          {shouldDisplayAddRemove ? (
            <ModalActionButton
              variant="outlined"
              size="small"
              style={{
                fontSize: "12px",
                fontWeight: 600,
                padding: "4px 12px",
                borderRadius: "32px",
                width: "100%",
              }}
              onClick={(ev) => {
                ev.stopPropagation();
                props?.onSelectPerk?.({
                  perkItem: props.perkItem,
                  manualSeatCount: props?.manualSeatCount,
                });
              }}
            >
              {isPerkItemSelected ? "Remove" : ` Add`}
            </ModalActionButton>
          ) : null}
        </Stack>
      </Box>
    </>
  );
};

export const PerkItemLogo = (props: { logoUrl: string }) => {
  return (
    <NextImage
      src={props.logoUrl}
      alt="perk-item-logo"
      width={48}
      height={48}
      style={{ objectFit: "contain", borderRadius: "4px" }}
      loading="lazy"
      placeholder="blur"
      blurDataURL={blurDataURL}
    />
  );
};
